import React, { useEffect, useRef, useState } from 'react';
import {
  Trash2, ChevronDown, Check, ArrowLeft, ArrowRight, Loader2, Paperclip, FileText,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { formatearTamano } from '../utils/helpers';
import {
  dniValido, emailValido, telefonoValido, formatearDni, tituloSimple, normalizarUsuario,
} from '../lib/validaciones';
import BuscadorProductos, { formatearPrecio } from './BuscadorProductos';
import { estaEmbebido, useAvisarAltura, useAvisarPaso } from '../lib/embed';
import { detalleProducto } from '../lib/detalleProducto';

/**
 * Formulario público de colaboraciones, en /formulario.
 *
 * Replica el formulario multipaso de n8n, incluidas sus dos bifurcaciones:
 *  - sin factura → pantalla final explicando que no se puede colaborar
 *  - dirección de facturación distinta → un paso más antes del final
 *
 * No usa los componentes del panel a propósito: esta pantalla la ve gente de
 * fuera, sin sesión, y conviene que sea autónoma y ligera.
 */

const PLATAFORMAS = ['Instagram', 'TikTok', 'YouTube'];

// Etiquetas de la barra de pasos. El quinto solo existe si la dirección de
// facturación va aparte, así que la barra se corta por `totalPasos`.
const PASOS = ['Perfil', 'Producto', 'Datos', 'Envío', 'Facturación'];

// ¿El producto elegido es un sofá? Manda la categoría del feed; el nombre es el
// respaldo para las fichas que la traen vacía. Sin tildes porque llega escrito
// de las dos maneras ("Sofás", "sofa cama").
const sinTildes = (valor) =>
  String(valor || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function esSofa(producto) {
  return sinTildes(`${producto.categoria || ''} ${producto.nombre || ''}`).includes('sofa');
}
const MAX_PRODUCTOS = 5;

// Archivos que el influencer puede adjuntar (media kit, tarifas...). Mismos
// topes que en server/lib/adjuntosSolicitud.js, que es el que manda. Si cambias
// uno, cambia el otro.
const MAX_ADJUNTOS = 3;
const MAX_ADJUNTOS_MB = 20;
const EXTENSIONES_ADJUNTO = ['.pdf', '.docx'];
const ACEPTA_ADJUNTO =
  '.pdf,.docx,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const FACTURA_SI = 'Sí, puedo emitir factura';
const FACTURA_NO = 'No puedo emitir factura';
const MISMA_DIRECCION = 'La misma que la de envío';
const OTRA_DIRECCION = 'Otra dirección distinta';

const INICIAL = {
  usuario: '',
  plataforma: 'Instagram',
  audiencia: '',
  factura: '',
  producto_url: '',
  productos: [],
  nombre: '',
  dni: '',
  email: '',
  telefono: '',
  direccion: '',
  codigo_postal: '',
  ciudad: '',
  provincia: '',
  tipo_facturacion: MISMA_DIRECCION,
  direccion_facturacion: '',
  codigo_postal_facturacion: '',
  ciudad_facturacion: '',
  provincia_facturacion: '',
};

// ===== Piezas de interfaz =====

function Campo({ etiqueta, obligatorio, children, error, className }) {
  return (
    <div className={cn('min-w-0', className)}>
      <label className="mb-1.5 block text-sm font-medium text-[#344054]">
        {etiqueta}
        {obligatorio && <span className="text-[#d2003b]"> *</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-[#ea1f30]">{error}</p>}
    </div>
  );
}

/** Rejilla de campos: una columna en móvil, dos en cuanto hay sitio. */
function Fila({ children }) {
  return <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">{children}</div>;
}

const claseEntrada =
  'w-full rounded-lg border border-[#d7dbe3] bg-white px-3.5 py-3 text-[15px] text-[#101828] ' +
  'placeholder:text-[#98a2b3] outline-none transition-colors ' +
  'focus:border-[#d2003b] focus:ring-4 focus:ring-[#d2003b]/8';

function Texto({ valor, onChange, placeholder, tipo = 'text', error, ...resto }) {
  return (
    <input
      type={tipo}
      value={valor}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(claseEntrada, error && 'border-[#ea1f30]')}
      {...resto}
    />
  );
}

/**
 * Opciones en forma de píldora. El <input type="radio"> sigue ahí, escondido:
 * la píldora es solo la piel, y así se conserva la navegación con teclado y el
 * agrupado por `name` que da un radio de verdad.
 */
function Opciones({ valor, onChange, opciones, nombre }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {opciones.map((opcion) => (
        <label
          key={opcion}
          className={cn(
            'cursor-pointer rounded-full border px-4 py-2.5 text-sm transition-colors',
            'focus-within:ring-4 focus-within:ring-[#d2003b]/10',
            valor === opcion
              ? 'border-[#f0c9d6] bg-[#fdecf1] font-semibold text-[#d2003b]'
              : 'border-[#dbdfe7] bg-white text-[#555] hover:border-[#bfc5d0]',
          )}
        >
          <input
            type="radio"
            name={nombre}
            checked={valor === opcion}
            onChange={() => onChange(opcion)}
            className="sr-only"
          />
          {opcion}
        </label>
      ))}
    </div>
  );
}

/**
 * Desplegable propio en vez de un <select> nativo: el del sistema no se puede
 * estilar y en móvil abre la rueda del dispositivo, que desentona con el resto.
 * Se cierra al elegir, al pulsar fuera y con Escape.
 */
function Desplegable({ valor, onChange, opciones, id }) {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false);
    };
    const escape = (e) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto]);

  return (
    <div ref={contenedor} className="relative">
      <button
        id={id}
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        className={cn(claseEntrada, 'flex items-center justify-between text-left')}
      >
        <span>{valor}</span>
        <ChevronDown
          className={cn('h-4 w-4 text-[#7e8186] transition-transform', abierto && 'rotate-180')}
        />
      </button>

      {abierto && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-lg border
                     border-[#dbdfe7] bg-white shadow-lg"
        >
          {opciones.map((opcion) => (
            <li key={opcion}>
              <button
                type="button"
                role="option"
                aria-selected={valor === opcion}
                onClick={() => {
                  onChange(opcion);
                  setAbierto(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3.5 py-2.5 text-left text-[15px]',
                  valor === opcion
                    ? 'bg-[#fdf3f6] font-medium text-[#d2003b]'
                    : 'text-[#555] hover:bg-[#f7f8fa]',
                )}
              >
                {opcion}
                {valor === opcion && <Check className="h-4 w-4" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Archivos adicionales: se eligen con el botón o se arrastran a la caja. Los
 * que no valen no se añaden, y se dice por qué; el servidor vuelve a
 * comprobarlo todo.
 */
function Adjuntos({ archivos, onCambiar }) {
  const entrada = useRef(null);
  const [aviso, setAviso] = useState('');
  const [encima, setEncima] = useState(false);

  function anadir(lista) {
    const siguientes = [...archivos];
    let problema = '';
    Array.from(lista).forEach((archivo) => {
      const nombre = archivo.name || '';
      const extension = nombre.slice(nombre.lastIndexOf('.')).toLowerCase();
      const ocupado = siguientes.reduce((suma, a) => suma + a.size, 0);
      if (siguientes.some((a) => a.name === nombre && a.size === archivo.size)) return;
      if (!EXTENSIONES_ADJUNTO.includes(extension)) {
        problema = `"${nombre}" no es un PDF ni un Word (.docx)`;
      } else if (siguientes.length >= MAX_ADJUNTOS) {
        problema = `Como máximo puedes adjuntar ${MAX_ADJUNTOS} archivos`;
      } else if (ocupado + archivo.size > MAX_ADJUNTOS_MB * 1024 * 1024) {
        problema = `"${nombre}" no cabe: entre todos los archivos el máximo es ${MAX_ADJUNTOS_MB} MB`;
      } else {
        siguientes.push(archivo);
      }
    });
    setAviso(problema);
    onCambiar(siguientes);
  }

  function quitar(indice) {
    setAviso('');
    onCambiar(archivos.filter((_, i) => i !== indice));
  }

  return (
    <div>
      {archivos.length > 0 && (
        <ul className="mb-2.5 space-y-2">
          {archivos.map((archivo, i) => (
            <li
              key={`${archivo.name}-${archivo.size}`}
              className="flex items-center gap-3 rounded-lg border border-[#e4e8ef] bg-white px-3.5 py-3"
            >
              <FileText className="h-4 w-4 shrink-0 text-[#d2003b]" />
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-[#101828]">{archivo.name}</p>
              <span className="shrink-0 text-xs text-[#7e8186]">{formatearTamano(archivo.size)}</span>
              <button
                type="button"
                onClick={() => quitar(i)}
                title="Quitar"
                className="shrink-0 p-0.5 text-[#7e8186] hover:text-[#ea1f30]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {archivos.length < MAX_ADJUNTOS && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setEncima(true);
          }}
          onDragLeave={() => setEncima(false)}
          onDrop={(e) => {
            e.preventDefault();
            setEncima(false);
            anadir(e.dataTransfer.files);
          }}
          className={cn(
            'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-dashed px-3.5 py-3 transition-colors',
            encima ? 'border-[#d2003b] bg-[#fdf3f6]' : 'border-[#d7dbe3] bg-white',
          )}
        >
          <button
            type="button"
            onClick={() => entrada.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg border border-[#dbdfe7] bg-white px-3.5 py-2
                       text-sm font-medium text-[#344054] transition-colors hover:border-[#bfc5d0]"
          >
            <Paperclip className="h-4 w-4" />
            Añadir archivos
          </button>
          <span className="text-sm text-[#98a2b3]">o arrástralos aquí</span>
        </div>
      )}

      <input
        ref={entrada}
        type="file"
        multiple
        accept={ACEPTA_ADJUNTO}
        tabIndex={-1}
        className="hidden"
        onChange={(e) => {
          anadir(e.target.files);
          // Sin esto, volver a elegir el mismo archivo tras quitarlo no haría nada.
          e.target.value = '';
        }}
      />

      <p className="mt-1.5 text-xs text-[#7e8186]">
        No es obligatorio. Tu media kit, tus tarifas o lo que quieras enseñarnos: PDF o Word
        (.docx), hasta {MAX_ADJUNTOS} archivos y {MAX_ADJUNTOS_MB} MB entre todos.
      </p>
      {aviso && <p className="mt-1.5 text-xs text-[#ea1f30]">{aviso}</p>}
    </div>
  );
}

/**
 * Envía el formulario y devuelve el código y el cuerpo de la respuesta. Va con
 * XMLHttpRequest y no con fetch porque fetch no informa de cuánto lleva subido,
 * y con varios MB de archivos hay que ver que avanza.
 */
function enviarFormulario(cuerpo, onProgreso) {
  return new Promise((resolve, reject) => {
    const peticion = new XMLHttpRequest();
    peticion.open('POST', '/api/formulario');
    if (onProgreso) {
      peticion.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgreso(Math.round((e.loaded / e.total) * 100));
      };
    }
    peticion.onload = () => {
      let json = null;
      try {
        json = JSON.parse(peticion.responseText);
      } catch {
        // nginx responde en HTML cuando corta por tamaño: se queda en null.
      }
      resolve({ status: peticion.status, cuerpo: json });
    };
    peticion.onerror = () =>
      reject(new Error('Se ha cortado la conexión. Revisa tu conexión e inténtalo de nuevo.'));
    peticion.send(cuerpo);
  });
}

function Boton({ children, cargando, ...resto }) {
  return (
    <button
      type="submit"
      className="inline-flex items-center gap-2 rounded-lg bg-[#d2003b] px-6 py-3 text-[15px]
                 font-semibold text-white transition-colors hover:bg-[#b30033]
                 disabled:cursor-not-allowed disabled:opacity-60"
      {...resto}
    >
      {cargando ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <ArrowRight className="h-4 w-4" />
      )}
      {children}
    </button>
  );
}

/**
 * Pie de navegación, fuera de la tarjeta: "Atrás" a la izquierda y la acción
 * principal a la derecha. Atrás es type="button" a propósito —dentro de un
 * <form> el tipo por defecto es submit, y volver no debe validar ni enviar—.
 * En el primer paso no hay Atrás, pero el hueco se mantiene para que
 * "Siguiente" no se descoloque.
 */
function Navegacion({ atras, children }) {
  return (
    <div className="mt-6 flex items-center justify-between gap-3">
      {atras ? (
        <button
          type="button"
          onClick={atras}
          className="inline-flex items-center gap-2 rounded-lg px-3 py-3 text-[15px] font-semibold
                     text-[#344054] transition-colors hover:bg-[#eef0f4]"
        >
          <ArrowLeft className="h-4 w-4" />
          Atrás
        </button>
      ) : (
        <span />
      )}
      {children}
    </div>
  );
}

/**
 * Sofá visto de frente con el chaise longue —el módulo bajo y alargado de la
 * derecha— resaltado en el rojo de la marca. Va en SVG y sin dependencias: el
 * formulario público lo ve gente de fuera y conviene no cargarlo de peso.
 */
function IlustracionChaiseLongue() {
  return (
    <svg
      viewBox="0 0 260 130"
      role="img"
      aria-label="Sofá visto de frente con el chaise longue a la derecha"
      className="w-full max-w-[240px]"
    >
      {/* Cuerpo del sofá: gris, en segundo plano */}
      <rect x="16" y="46" width="20" height="50" rx="9" fill="#e7eaf0" stroke="#dbdfe7" />
      <rect x="28" y="24" width="116" height="42" rx="10" fill="#f1f3f7" stroke="#dbdfe7" />
      <rect x="22" y="60" width="128" height="30" rx="8" fill="#e7eaf0" stroke="#dbdfe7" />
      <line x1="86" y1="62" x2="86" y2="88" stroke="#dbdfe7" />
      <rect x="34" y="96" width="8" height="10" rx="2" fill="#dbdfe7" />
      <rect x="130" y="96" width="8" height="10" rx="2" fill="#dbdfe7" />

      {/* Chaise longue: sin respaldo, más bajo y hacia el frente */}
      <rect x="150" y="52" width="88" height="14" rx="6" fill="#f7c9d6" stroke="#d2003b" />
      <rect x="146" y="62" width="96" height="32" rx="8" fill="#fdf3f6" stroke="#d2003b" strokeWidth="1.5" />
      <rect x="228" y="46" width="18" height="48" rx="8" fill="#f7c9d6" stroke="#d2003b" />
      <rect x="156" y="96" width="8" height="10" rx="2" fill="#d2003b" opacity="0.35" />
      <rect x="224" y="96" width="8" height="10" rx="2" fill="#d2003b" opacity="0.35" />

      <text x="194" y="122" textAnchor="middle" fontSize="11" fontWeight="600" fill="#d2003b">
        Derecha
      </text>
    </svg>
  );
}

/**
 * Aviso del lado del chaise longue. Sale solo cuando hay un sofá elegido: es el
 * error caro de este formulario —un sofá que llega con el chaise al revés no se
 * cambia— y se enseña antes de pasar de paso, no después.
 */
function AvisoChaiseLongue() {
  return (
    <div className="aviso-anim mt-5">
      <div>
        <div className="rounded-md border border-[#f0c9d6] bg-[#fdf3f6] p-4">
          <p className="text-sm font-semibold text-[#525356]">
            Revisa el lado del chaise longue
          </p>
          <p className="mt-1 text-xs leading-relaxed text-[#7e8186]">
            El lado del chaise longue es visto de frente. Si seleccionas Derecha, el lado del
            chaise longue estará, mirando el sofá de frente, a la derecha.
          </p>
          <div className="mt-3 flex justify-center">
            <IlustracionChaiseLongue />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Barra de pasos. Marca los ya completados con un check, el actual con el
 * número en rojo y los pendientes en gris. En móvil solo se lee la etiqueta del
 * paso actual: con cinco textos no cabrían y los círculos ya cuentan por dónde
 * va.
 */
function Pasos({ actual, total }) {
  return (
    <ol className="mb-5 flex items-center rounded-xl border border-[#e4e8ef] bg-white px-4 py-3.5 sm:px-5">
      {PASOS.slice(0, total).map((etiqueta, i) => {
        const numero = i + 1;
        const hecho = numero < actual;
        const activo = numero === actual;
        return (
          <React.Fragment key={etiqueta}>
            {i > 0 && (
              <span
                aria-hidden
                className={cn('mx-2 h-px flex-1 sm:mx-3', hecho || activo ? 'bg-[#d2003b]' : 'bg-[#e4e8ef]')}
              />
            )}
            <li className="flex shrink-0 items-center gap-2" aria-current={activo ? 'step' : undefined}>
              <span
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold',
                  hecho && 'bg-[#d2003b] text-white',
                  activo && 'border-2 border-[#d2003b] bg-white text-[#d2003b]',
                  !hecho && !activo && 'bg-[#eef1f6] text-[#98a2b3]',
                )}
              >
                {hecho ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : numero}
              </span>
              <span
                className={cn(
                  'text-sm whitespace-nowrap',
                  activo ? 'font-semibold text-[#101828]' : 'hidden text-[#7e8186] sm:inline',
                )}
              >
                {etiqueta}
              </span>
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  );
}

/**
 * Marco de todas las pantallas: cabecera fija, barra de pasos y el contenido
 * dentro de una tarjeta blanca. El pie de navegación va fuera de la tarjeta,
 * así que lo pone cada paso dentro de su propio <form>.
 */
function Pantalla({ paso, total, children }) {
  // Embebido, el marco vale justo lo que ocupa el contenido: `min-h-screen`
  // dentro de un iframe que se ajusta al contenido no dejaría encoger nunca.
  const marco = useRef(null);
  useAvisarAltura(marco);

  return (
    <div
      ref={marco}
      className={cn('bg-[#f7f8fa] px-4', estaEmbebido() ? 'py-6' : 'min-h-screen py-8 sm:py-12')}
    >
      <div className="mx-auto w-full max-w-[800px]">
        <h1 className="text-[26px] font-bold tracking-tight text-[#101828] sm:text-[32px]">
          Colaboraciones
        </h1>
        <p className="mt-1 mb-6 text-[15px] text-[#7e8186] sm:text-base">
          Solicita tu colaboración con La Tienda HOME
        </p>

        {paso && <Pasos actual={paso} total={total} />}
        {children}
      </div>
    </div>
  );
}

/** Tarjeta blanca del contenido, con una entradilla opcional arriba. */
function Tarjeta({ intro, children }) {
  return (
    <div className="rounded-xl border border-[#e4e8ef] bg-white p-5 shadow-[0_1px_3px_rgba(16,24,40,0.04)] sm:p-7">
      {intro && <p className="mb-5 text-[15px] text-[#7e8186]">{intro}</p>}
      {children}
    </div>
  );
}

// ===== Formulario =====

export default function FormularioPublico() {
  const [paso, setPaso] = useState(1);
  useAvisarPaso(paso);
  const [datos, setDatos] = useState(INICIAL);
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState('');
  // Los archivos van aparte de `datos`: son File y no se serializan con el resto.
  const [adjuntos, setAdjuntos] = useState([]);
  const [progreso, setProgreso] = useState(null);

  const poner = (clave) => (valor) => {
    setDatos((previo) => ({ ...previo, [clave]: valor }));
    setErrores((previo) => ({ ...previo, [clave]: undefined }));
  };

  // El último paso depende de si la facturación va aparte.
  const facturacionAparte = datos.tipo_facturacion === OTRA_DIRECCION;
  const totalPasos = facturacionAparte ? 5 : 4;

  // No se admite dos veces la misma variante del mismo producto.
  function anadirProducto(elegido) {
    setDatos((previo) => {
      if (previo.productos.length >= MAX_PRODUCTOS) return previo;
      const repetido = previo.productos.some(
        (p) => p.productId === elegido.productId && p.opId === elegido.opId,
      );
      return repetido ? previo : { ...previo, productos: [...previo.productos, elegido] };
    });
  }

  function quitarProducto(indice) {
    setDatos((previo) => ({
      ...previo,
      productos: previo.productos.filter((_, i) => i !== indice),
    }));
  }

  /**
   * Comprueba los campos de un paso. Cada entrada es
   * [clave, etiqueta] o [clave, etiqueta, { regla, aviso }].
   */
  function faltan(campos) {
    const fallos = {};
    campos.forEach(([clave, etiqueta, comprobacion]) => {
      const valor = String(datos[clave] || '').trim();
      if (!valor) {
        fallos[clave] = `${etiqueta} es obligatorio`;
      } else if (comprobacion && !comprobacion.regla(valor)) {
        fallos[clave] = comprobacion.aviso;
      }
    });
    setErrores(fallos);
    return Object.keys(fallos).length > 0;
  }

  async function enviar() {
    setEnviando(true);
    setErrorEnvio('');
    // Los datos van como JSON en un campo y los archivos al lado: multipart.
    const formulario = new FormData();
    formulario.append(
      'datos',
      JSON.stringify({
        ...datos,
        factura: 'Sí',
        // El servidor rehace los datos desde el catálogo; aquí basta el par de ids.
        productos: datos.productos.map((p) => ({ productId: p.productId, opId: p.opId })),
        producto_url: datos.productos.find((p) => p.url)?.url || '',
        // Si la facturación es la misma, no se mandan los campos extra.
        direccion_facturacion: facturacionAparte ? datos.direccion_facturacion : '',
        codigo_postal_facturacion: facturacionAparte ? datos.codigo_postal_facturacion : '',
        ciudad_facturacion: facturacionAparte ? datos.ciudad_facturacion : '',
        provincia_facturacion: facturacionAparte ? datos.provincia_facturacion : '',
      }),
    );
    adjuntos.forEach((archivo) => formulario.append('adjuntos', archivo, archivo.name));
    try {
      const { status, cuerpo } = await enviarFormulario(
        formulario,
        adjuntos.length > 0 ? setProgreso : null,
      );
      if (status < 200 || status >= 300) {
        throw new Error(
          cuerpo?.error ||
            (status === 413
              ? `Los archivos pesan demasiado: el máximo es ${MAX_ADJUNTOS_MB} MB entre todos`
              : 'No se pudo enviar el formulario'),
        );
      }
      setPaso('fin');
    } catch (err) {
      setErrorEnvio(err.message);
    } finally {
      setEnviando(false);
      setProgreso(null);
    }
  }

  // Con archivos, el botón cuenta cuánto lleva subido; al llegar al 100 % aún
  // falta que el servidor guarde, así que vuelve a "Enviando…".
  const textoEnviando =
    progreso != null && progreso < 100 ? `Enviando… ${progreso} %` : 'Enviando…';

  function alEnviar(e, siguiente) {
    e.preventDefault();
    siguiente();
  }

  // ===== Pantallas finales =====

  if (paso === 'sin-factura') {
    return (
      <Pantalla>
        <Tarjeta>
          <h2 className="mb-3 text-lg font-semibold text-[#101828]">
            No es posible realizar una colaboración
          </h2>
          <div className="space-y-3 text-[15px] leading-relaxed text-[#555]">
          <p>Lo sentimos, pero sin factura no podemos realizar una colaboración.</p>
          <p>
            Te agradecemos mucho tu interés y nos encantará revisar tu solicitud cuando puedas
            emitirla.
          </p>
            <p>¡Muchas gracias!</p>
          </div>
        </Tarjeta>
      </Pantalla>
    );
  }

  if (paso === 'fin') {
    return (
      <Pantalla>
        <Tarjeta>
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#fdecf1]">
              <Check className="h-5 w-5 text-[#d2003b]" strokeWidth={3} />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-[#101828]">¡Listo!</h2>
              <p className="mt-1 text-[15px] text-[#555]">
                En breves nos pondremos en contacto contigo.
              </p>
            </div>
          </div>
        </Tarjeta>
      </Pantalla>
    );
  }

  // ===== Paso 1: perfil y facturación =====

  if (paso === 1) {
    return (
      <Pantalla paso={1} total={totalPasos}>
        <form
          noValidate
          onSubmit={(e) =>
            alEnviar(e, () => {
              if (
                faltan([
                  ['usuario', 'El nombre de usuario', {
                    regla: (v) => Boolean(normalizarUsuario(v, datos.plataforma)),
                    aviso: 'Revisa el usuario: debe tener letras o números',
                  }],
                  ['audiencia', 'La audiencia'],
                  ['factura', 'La respuesta sobre la factura'],
                ])
              ) {
                return;
              }
              setPaso(datos.factura === FACTURA_NO ? 'sin-factura' : 2);
            })
          }
        >
          <Tarjeta intro="Cuéntanos quién eres y desde dónde publicas">
            <Fila>
              <Campo etiqueta="Nombre de usuario" obligatorio error={errores.usuario}>
                <Texto
                  valor={datos.usuario}
                  onChange={poner('usuario')}
                  placeholder="Escribe tu nombre de usuario..."
                  error={errores.usuario}
                  autoFocus
                />
              </Campo>

              <Campo etiqueta="Plataforma" obligatorio>
                <Desplegable
                  valor={datos.plataforma}
                  onChange={poner('plataforma')}
                  opciones={PLATAFORMAS}
                />
              </Campo>
            </Fila>

            <Campo
              etiqueta="Audiencia"
              obligatorio
              error={errores.audiencia}
              className="mt-5"
            >
              <Texto
                valor={datos.audiencia}
                onChange={poner('audiencia')}
                placeholder="Indica los países y porcentajes"
                error={errores.audiencia}
              />
            </Campo>

            <Campo etiqueta="Archivos adicionales" className="mt-5">
              <Adjuntos archivos={adjuntos} onCambiar={setAdjuntos} />
            </Campo>

          {/* Bloque informativo sobre facturación, igual que en n8n */}
          <div className="my-5 space-y-2 rounded-lg border border-[#e4e8ef] bg-[#f9fafc] p-4 text-xs leading-relaxed text-[#667085]">
            <p>
              Es importante tener en cuenta que una colaboración, a efectos legales, no es un
              intercambio sino un servicio publicitario pagado en especie, es decir, con producto.
            </p>
            <p>
              Por lo tanto, si deseas realizar una colaboración con nosotros, es crucial que puedas
              emitir una factura a tu nombre por la colaboración. Nosotros también te haremos una
              factura de vuelta, por lo que podrás desgravarte el IVA y la emisión de la factura
              tendrá un coste cero.
            </p>
            <p>El importe de la factura será del valor total del producto.</p>
            <p>
              Por ejemplo: realizando una colaboración pagada con un colchón de 500 euros, deberás
              emitir una factura cuyo importe total, incluyendo el IVA, sea de 500 euros. Por
              nuestra parte, emitiremos una factura de vuelta de 500 € incluyendo el IVA.
            </p>
          </div>

            <Campo etiqueta="Factura" obligatorio error={errores.factura}>
              <Opciones
                nombre="factura"
                valor={datos.factura}
                onChange={poner('factura')}
                opciones={[FACTURA_SI, FACTURA_NO]}
              />
            </Campo>
          </Tarjeta>

          <Navegacion>
            <Boton>Siguiente</Boton>
          </Navegacion>
        </form>
      </Pantalla>
    );
  }

  // ===== Paso 2: producto =====

  if (paso === 2) {
    return (
      <Pantalla paso={2} total={totalPasos}>
        <form
          noValidate
          onSubmit={(e) =>
            alEnviar(e, () => setPaso(3))
          }
        >
          <Tarjeta intro="Solicita el producto que tienes en mente">
            <Campo etiqueta="Producto que te interesa">
              {datos.productos.length > 0 && (
                <ul className="mb-2.5 space-y-2">
                  {datos.productos.map((p, i) => (
                    <li
                      key={`${p.productId}-${p.opId}-${i}`}
                      className="flex items-center gap-3 rounded-lg border border-[#e4e8ef] bg-white px-3.5 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-[#101828]">{p.nombre}</p>
                        <p className="mt-0.5 break-words text-xs text-[#7e8186]">
                          {detalleProducto(p)}
                        </p>
                      </div>
                      <span className="shrink-0 text-sm font-semibold text-[#101828]">
                        {formatearPrecio(p.precio)}
                      </span>
                      <button
                        type="button"
                        onClick={() => quitarProducto(i)}
                        title="Quitar"
                        className="shrink-0 p-0.5 text-[#7e8186] hover:text-[#ea1f30]"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <BuscadorProductos
                onElegir={anadirProducto}
                deshabilitado={datos.productos.length >= MAX_PRODUCTOS}
                textoDeshabilitado={`Ya has añadido el máximo de ${MAX_PRODUCTOS} productos. Quita alguno para cambiarlo.`}
              />
              <p className="mt-1.5 text-xs text-[#7e8186]">
                Busca por nombre o pega el enlace del producto y elige la medida, el color y el
                tapizado que quieras. No es obligatorio: si aún no lo tienes claro, puedes seguir
                sin elegir ninguno.
              </p>
            </Campo>

            {datos.productos.some(esSofa) && <AvisoChaiseLongue />}
          </Tarjeta>

          <Navegacion atras={() => setPaso(1)}>
            <Boton>Siguiente</Boton>
          </Navegacion>
        </form>
      </Pantalla>
    );
  }

  // ===== Paso 3: datos personales =====

  if (paso === 3) {
    return (
      <Pantalla paso={3} total={totalPasos}>
        <form
          noValidate
          onSubmit={(e) =>
            alEnviar(e, () => {
              if (
                faltan([
                  ['nombre', 'El nombre y apellidos'],
                  ['dni', 'El DNI', {
                    regla: dniValido,
                    aviso: 'Revisa el DNI: no parece un número válido',
                  }],
                  ['email', 'El email', {
                    regla: emailValido,
                    aviso: 'Revisa el email: falta la arroba o el dominio',
                  }],
                  ['telefono', 'El teléfono', {
                    regla: telefonoValido,
                    aviso: 'El teléfono debe tener al menos 9 dígitos',
                  }],
                ])
              ) {
                return;
              }
              setPaso(4);
            })
          }
        >
          <Tarjeta intro="Datos personales para el contrato">
            <Fila>
              <Campo etiqueta="Nombre y apellidos" obligatorio error={errores.nombre}>
                <Texto
                  valor={datos.nombre}
                  onChange={poner('nombre')}
                  onBlur={() => poner('nombre')(tituloSimple(datos.nombre))}
                  placeholder="Escribe tu nombre completo y apellidos..."
                  error={errores.nombre}
                  autoFocus
                />
              </Campo>

              <Campo etiqueta="DNI" obligatorio error={errores.dni}>
                <Texto
                  valor={datos.dni}
                  onChange={poner('dni')}
                  onBlur={() => poner('dni')(formatearDni(datos.dni))}
                  placeholder="12345678A"
                  error={errores.dni}
                />
              </Campo>

              <Campo etiqueta="Email" obligatorio error={errores.email}>
                <Texto
                  tipo="email"
                  valor={datos.email}
                  onChange={poner('email')}
                  placeholder="Escribe tu correo electrónico..."
                  error={errores.email}
                />
              </Campo>

              <Campo etiqueta="Teléfono" obligatorio error={errores.telefono}>
                <Texto
                  tipo="tel"
                  valor={datos.telefono}
                  onChange={poner('telefono')}
                  placeholder="Escribe tu número de teléfono..."
                  error={errores.telefono}
                />
              </Campo>
            </Fila>
          </Tarjeta>

          <Navegacion atras={() => setPaso(2)}>
            <Boton>Siguiente</Boton>
          </Navegacion>
        </form>
      </Pantalla>
    );
  }

  // ===== Paso 4: dirección de envío =====

  if (paso === 4) {
    return (
      <Pantalla paso={4} total={totalPasos}>
        <form
          noValidate
          onSubmit={(e) =>
            alEnviar(e, () => {
              if (
                faltan([
                  ['direccion', 'La dirección'],
                  ['codigo_postal', 'El código postal'],
                  ['ciudad', 'La ciudad'],
                  ['provincia', 'La provincia'],
                ])
              ) {
                return;
              }
              if (datos.tipo_facturacion === OTRA_DIRECCION) setPaso(5);
              else enviar();
            })
          }
        >
          <Tarjeta intro="¿Dónde te enviamos el producto?">
            <Campo etiqueta="Dirección" obligatorio error={errores.direccion}>
              <Texto
                valor={datos.direccion}
                onChange={poner('direccion')}
                placeholder="Escribe tu calle, incluyendo el número y la puerta..."
                error={errores.direccion}
                autoFocus
              />
            </Campo>

            <Fila>
              <Campo
                etiqueta="Código postal"
                obligatorio
                error={errores.codigo_postal}
                className="mt-5"
              >
                <Texto
                  tipo="text"
                  inputMode="numeric"
                  valor={datos.codigo_postal}
                  onChange={poner('codigo_postal')}
                  placeholder="Escribe tu código postal..."
                  error={errores.codigo_postal}
                />
              </Campo>

              <Campo etiqueta="Ciudad" obligatorio error={errores.ciudad} className="sm:mt-5">
                <Texto
                  valor={datos.ciudad}
                  onChange={poner('ciudad')}
                  placeholder="Escribe tu ciudad..."
                  error={errores.ciudad}
                />
              </Campo>
            </Fila>

            <Campo
              etiqueta="Provincia"
              obligatorio
              error={errores.provincia}
              className="mt-5"
            >
              <Texto
                valor={datos.provincia}
                onChange={poner('provincia')}
                placeholder="Escribe tu provincia..."
                error={errores.provincia}
              />
            </Campo>

            <div className="mt-6 border-t border-[#eef0f4] pt-5">
              <Campo etiqueta="Dirección de facturación" obligatorio>
                <Opciones
                  nombre="tipo_facturacion"
                  valor={datos.tipo_facturacion}
                  onChange={poner('tipo_facturacion')}
                  opciones={[MISMA_DIRECCION, OTRA_DIRECCION]}
                />
              </Campo>
            </div>
          </Tarjeta>

          {errorEnvio && <p className="mt-3 text-sm text-[#ea1f30]">{errorEnvio}</p>}
          <Navegacion atras={() => setPaso(3)}>
            <Boton disabled={enviando} cargando={enviando}>
              {enviando
                ? textoEnviando
                : datos.tipo_facturacion === OTRA_DIRECCION
                  ? 'Siguiente'
                  : 'Enviar'}
            </Boton>
          </Navegacion>
        </form>
      </Pantalla>
    );
  }

  // ===== Paso 5: dirección de facturación =====

  return (
    <Pantalla paso={5} total={totalPasos}>
      <form
        noValidate
        onSubmit={(e) =>
          alEnviar(e, () => {
            if (
              faltan([
                ['direccion_facturacion', 'La dirección'],
                ['codigo_postal_facturacion', 'El código postal'],
                ['ciudad_facturacion', 'La ciudad'],
                ['provincia_facturacion', 'La provincia'],
              ])
            ) {
              return;
            }
            enviar();
          })
        }
      >
        <Tarjeta intro="Dirección de facturación">
          <Campo etiqueta="Dirección" obligatorio error={errores.direccion_facturacion}>
            <Texto
              valor={datos.direccion_facturacion}
              onChange={poner('direccion_facturacion')}
              placeholder="Escribe la dirección de facturación..."
              error={errores.direccion_facturacion}
              autoFocus
            />
          </Campo>

          <Fila>
            <Campo
              etiqueta="Código postal"
              obligatorio
              error={errores.codigo_postal_facturacion}
              className="mt-5"
            >
              <Texto
                inputMode="numeric"
                valor={datos.codigo_postal_facturacion}
                onChange={poner('codigo_postal_facturacion')}
                placeholder="Escribe el código postal de facturación..."
                error={errores.codigo_postal_facturacion}
              />
            </Campo>

            <Campo
              etiqueta="Ciudad"
              obligatorio
              error={errores.ciudad_facturacion}
              className="sm:mt-5"
            >
              <Texto
                valor={datos.ciudad_facturacion}
                onChange={poner('ciudad_facturacion')}
                placeholder="Escribe la ciudad de facturación..."
                error={errores.ciudad_facturacion}
              />
            </Campo>
          </Fila>

          <Campo
            etiqueta="Provincia"
            obligatorio
            error={errores.provincia_facturacion}
            className="mt-5"
          >
            <Texto
              valor={datos.provincia_facturacion}
              onChange={poner('provincia_facturacion')}
              placeholder="Escribe la provincia de facturación..."
              error={errores.provincia_facturacion}
            />
          </Campo>
        </Tarjeta>

        {errorEnvio && <p className="mt-3 text-sm text-[#ea1f30]">{errorEnvio}</p>}
        <Navegacion atras={() => setPaso(4)}>
          <Boton disabled={enviando} cargando={enviando}>
            {enviando ? textoEnviando : 'Enviar'}
          </Boton>
        </Navegacion>
      </form>
    </Pantalla>
  );
}
